import { useCallback, useMemo, useState, forwardRef } from 'react';

import { ScreenElementFragment } from '@/generated/graphql';
import { useEffect } from 'react';
import { createStyleUpdate } from '@/utils/createStyleUpdate';

export const ScreenElementBoundingBox = forwardRef<
  HTMLDivElement,
  {
    selected: boolean;
    onSelect: (elementId: string) => void;
    element: ScreenElementFragment;
    activeEngagement: boolean;
    onUpdate: (element: ScreenElementFragment) => void;
    onDoubleClick?: () => void;
    onDragStart?: () => void;
    onDragEnd?: () => void;
  }
>(function ScreenElementBoundingBox(
  {
    selected,
    onSelect,
    element,
    activeEngagement,
    onUpdate,
    onDoubleClick,
    onDragStart,
    onDragEnd,
  },
  wrapperRef,
) {
  const { pxToVw, pxToVh } = usePixelToViewport();
  const handleClick = useCallback(() => {
    if (!selected) {
      onSelect(element.id);
    }
    return true;
  }, [selected, onSelect, element.id]);
  const moveElementHandlers = useMoveElementHandlers({
    elementRef: wrapperRef as React.RefObject<HTMLDivElement>,
    onUpdatePosition: useCallback(
      ({ x, y }) => {
        const updates = createStyleUpdate(
          {
            left: `${pxToVw(x)}vw`,
            top: `${pxToVh(y)}vh`,
          },
          element,
          activeEngagement,
        );
        console.log('onUpdatePosition', updates);

        onUpdate({
          ...element,
          ...updates,
        });
      },
      [pxToVw, pxToVh, activeEngagement, onUpdate, element],
    ),
    onDragStart,
    onDragEnd,
  });

  const onUpdateSize = useCallback(
    ({
      top,
      left,
      width,
      height,
    }: Pick<DOMRect, 'width' | 'height' | 'top' | 'left'>) => {
      if (!selected) {
        onSelect(element.id);
      }
      const updates = createStyleUpdate(
        {
          width: `${pxToVw(width)}vw`,
          height: `${pxToVh(height)}vh`,
          top: `${pxToVh(top)}vh`,
          left: `${pxToVw(left)}vw`,
        },
        element,
        activeEngagement,
      );

      onUpdate({
        ...element,
        ...updates,
      });
    },
    [selected, activeEngagement, element, pxToVw, pxToVh, onUpdate, onSelect],
  );

  const scaleSEHandlers = useResizeElementHandlers({
    elementRef: wrapperRef as React.RefObject<HTMLDivElement>,
    anchorLocation: 'se',
    onUpdateSize,
    onDragStart,
    onDragEnd,
  });
  const scaleSWHandlers = useResizeElementHandlers({
    elementRef: wrapperRef as React.RefObject<HTMLDivElement>,
    anchorLocation: 'sw',
    onUpdateSize,
    onDragStart,
    onDragEnd,
  });
  const scaleNEHandlers = useResizeElementHandlers({
    elementRef: wrapperRef as React.RefObject<HTMLDivElement>,
    anchorLocation: 'ne',
    onUpdateSize,
    onDragStart,
    onDragEnd,
  });
  const scaleNWHandlers = useResizeElementHandlers({
    elementRef: wrapperRef as React.RefObject<HTMLDivElement>,
    anchorLocation: 'nw',
    onUpdateSize,
    onDragStart,
    onDragEnd,
  });
  const scaleNHandlers = useResizeElementHandlers({
    elementRef: wrapperRef as React.RefObject<HTMLDivElement>,
    anchorLocation: 'n',
    onUpdateSize,
    onDragStart,
    onDragEnd,
  });
  const scaleEHandlers = useResizeElementHandlers({
    elementRef: wrapperRef as React.RefObject<HTMLDivElement>,
    anchorLocation: 'e',
    onUpdateSize,
    onDragStart,
    onDragEnd,
  });
  const scaleSHandlers = useResizeElementHandlers({
    elementRef: wrapperRef as React.RefObject<HTMLDivElement>,
    anchorLocation: 's',
    onUpdateSize,
    onDragStart,
    onDragEnd,
  });
  const scaleWHandlers = useResizeElementHandlers({
    elementRef: wrapperRef as React.RefObject<HTMLDivElement>,
    anchorLocation: 'w',
    onUpdateSize,
    onDragStart,
    onDragEnd,
  });

  const selectedHandleClass = selected
    ? 'bg-blue-600 w-2 h-2'
    : 'opacity-0 hover:opacity-50';

  const selectedBoundingBoxClass = selected
    ? 'opacity-100'
    : 'opacity-0 hover:opacity-50';

  return (
    <div className={`absolute h-full w-full`}>
      {/* bounding box */}
      <div
        {...moveElementHandlers}
        data-name="BOUNDING_BOX"
        onDoubleClick={onDoubleClick}
        onClick={handleClick}
        className={`absolute h-full w-full cursor-move border border-gray-300 ${selectedBoundingBoxClass} `}
      ></div>
      {/* corner handles */}
      <div
        data-name="BOUNDING_BOX-NW-HANDLE"
        className={`absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 cursor-nw-resize rounded-full border border-white ${selectedHandleClass} `}
        {...scaleNWHandlers}
      ></div>
      <div
        data-name="BOUNDING_BOX-NE-HANDLE"
        className={`absolute right-0 top-0 -translate-y-1/2 translate-x-1/2 cursor-ne-resize rounded-full border border-white ${selectedHandleClass} `}
        {...scaleNEHandlers}
      ></div>
      <div
        data-name="BOUNDING_BOX-SW-HANDLE"
        className={`absolute bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-sw-resize rounded-full border border-white ${selectedHandleClass} `}
        {...scaleSWHandlers}
      ></div>
      <div
        data-name="BOUNDING_BOX-SE-HANDLE"
        className={`absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 cursor-se-resize rounded-full border border-white ${selectedHandleClass} `}
        {...scaleSEHandlers}
      ></div>
      <div
        data-name="BOUNDING_BOX-N-HANDLE"
        className={`absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 cursor-n-resize rounded-full border border-white ${selectedHandleClass} `}
        {...scaleNHandlers}
      ></div>

      <div
        data-name="BOUNDING_BOX-E-HANDLE"
        className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 cursor-e-resize rounded-full border border-white ${selectedHandleClass} `}
        {...scaleEHandlers}
      ></div>

      <div
        data-name="BOUNDING_BOX-S-HANDLE"
        className={`absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 cursor-s-resize rounded-full border border-white ${selectedHandleClass} `}
        {...scaleSHandlers}
      ></div>

      <div
        data-name="BOUNDING_BOX-W-HANDLE"
        className={`absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-w-resize rounded-full border border-white ${selectedHandleClass} `}
        {...scaleWHandlers}
      ></div>
    </div>
  );
});

/**
 * Returns a set of functions to convert between pixels and viewport units.
 *
 * @returns An object containing functions to convert between pixels and viewport units.
 */
function usePixelToViewport() {
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    // on window resize, update the viewport width and height
    const handleResize = () => {
      console.log('window resized');
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return useMemo(() => {
    const pxToVw = (px: number) => (px / viewportWidth) * 100;
    const pxToVh = (px: number) => (px / viewportHeight) * 100;
    const vhToPx = (vh: number) => (vh / 100) * viewportHeight;
    const vwToPx = (vw: number) => (vw / 100) * viewportWidth;

    return { pxToVw, pxToVh, vhToPx, vwToPx };
  }, [viewportHeight, viewportWidth]);
}

/**
 * Returns a set of functions to handle moving an element.
 *
 * @param onUpdatePosition - A function to update the element's position.
 * @param elementRef - A ref to the element to move.
 * @param bboxRef - A ref to the editing bounding box of the element.
 * @returns An object containing functions to handle moving an element.
 */
function useMoveElementHandlers({
  onUpdatePosition,
  elementRef,
  onDragStart,
  onDragEnd,
}: {
  onUpdatePosition: (position: { x: number; y: number }) => void;
  elementRef: React.RefObject<HTMLDivElement>;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // ignore right clicks
    if (e.button === 2) {
      return;
    }

    onDragStart?.();

    const startX = e.clientX;
    const startY = e.clientY;

    // get the element's bounding client rect
    const { top, left } = elementRef.current?.getBoundingClientRect() ?? {
      top: 0,
      left: 0,
    };

    // create a listener on window for mouse move
    const onMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      // update the element's transform position
      if (elementRef.current && 'style' in elementRef.current) {
        elementRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
      }
    };
    window.addEventListener('mousemove', onMouseMove);

    // create a listener on window for mouse up
    const onMouseUp = (e: MouseEvent) => {
      onDragEnd?.();

      // get final delta
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      // call onUpdatePosition with new x and y
      onUpdatePosition({ x: left + deltaX, y: top + deltaY });

      // reset the element's transform position
      if (elementRef.current && 'style' in elementRef.current) {
        elementRef.current.style.transform = 'translate(0px, 0px)';
      }

      // remove the mouse move listener
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mouseup', onMouseUp);
  };

  return {
    onMouseDown,
  };
}

function useResizeElementHandlers({
  elementRef,
  onUpdateSize,
  anchorLocation,
  onDragStart,
  onDragEnd,
}: {
  elementRef: React.RefObject<HTMLDivElement>;
  onUpdateSize: (
    boundingBox: Pick<DOMRect, 'width' | 'height' | 'top' | 'left'>,
  ) => void;
  anchorLocation: 'nw' | 'ne' | 'sw' | 'se' | 'n' | 'e' | 's' | 'w';
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // ignore right clicks
    if (e.button === 2) {
      return;
    }

    let symmetricScaling = e.altKey;

    onDragStart?.();

    const startX = e.clientX;
    const startY = e.clientY;

    // get the element's bounding client rect
    const { width, height, top, left } =
      elementRef.current?.getBoundingClientRect() ?? {
        width: 0,
        height: 0,
        top: 0,
        left: 0,
      };

    // remove the `transition-all` class from the element
    if (elementRef.current && 'classList' in elementRef.current) {
      // elementRef.current.classList.remove("transition-all");
    }

    let newWidth = width;
    let newHeight = height;
    let newTop = top;
    let newLeft = left;

    const mouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      let deltaY = e.clientY - startY;

      if (e.shiftKey) {
        deltaY = (deltaX * height) / width;
      }

      // if n, change height and top
      if (anchorLocation.includes('n')) {
        if (symmetricScaling) {
          newHeight = height - deltaY * 2;
          newTop = top + deltaY;
        } else {
          newHeight = height - deltaY;
          newTop = top + deltaY;
        }
      }
      // if s, change height
      if (anchorLocation.includes('s')) {
        if (symmetricScaling) {
          newHeight = height + deltaY * 2;
          newTop = top - deltaY;
        } else {
          newHeight = height + deltaY;
        }
      }
      // if w, change width and left
      if (anchorLocation.includes('w')) {
        if (symmetricScaling) {
          newWidth = width - deltaX * 2;
          newLeft = left + deltaX;
        } else {
          newWidth = width - deltaX;
          newLeft = left + deltaX;
        }
      }
      // if e, change width
      if (anchorLocation.includes('e')) {
        if (symmetricScaling) {
          newWidth = width + deltaX * 2;
          newLeft = left - deltaX;
        } else {
          newWidth = width + deltaX;
        }
      }

      newWidth = Math.max(newWidth, 5);
      newHeight = Math.max(newHeight, 5);

      if (elementRef.current && 'style' in elementRef.current) {
        elementRef.current.style.width = `${newWidth}px`;
        elementRef.current.style.height = `${newHeight}px`;
        elementRef.current.style.top = `${newTop}px`;
        elementRef.current.style.left = `${newLeft}px`;
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        symmetricScaling = true;
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        symmetricScaling = false;
      }
    };
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousemove', mouseMove);

    const onMouseUp = () => {
      onDragEnd?.();
      window.removeEventListener('mousemove', mouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);

      onUpdateSize({
        width: newWidth,
        height: newHeight,
        top: newTop,
        left: newLeft,
      });
    };
    window.addEventListener('mouseup', onMouseUp);
  };

  return {
    onMouseDown,
  };
}
